import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import SummaryCard from './SummaryCard';
import './Dashboard.css';

const API_URL = "https://shopify-ingestion-production.up.railway.app/api";

// Colors for pie chart
const COLORS = ['#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

function Dashboard({ tenantId, onLogout }) {
  const [summary, setSummary] = useState({});
  const [topCustomers, setTopCustomers] = useState([]);
  const [ordersByDate, setOrdersByDate] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Date filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardData();
  }, [tenantId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch summary
      const summaryRes = await axios.get(`${API_URL}/analytics/summary/${tenantId}`);
      setSummary(summaryRes.data);

      // Fetch top customers
      const customersRes = await axios.get(`${API_URL}/analytics/top-customers/${tenantId}?limit=5`);
      setTopCustomers(customersRes.data);

      // Fetch orders by date (with date filter if set)
      let ordersUrl = `${API_URL}/analytics/orders-by-date/${tenantId}`;
      if (startDate && endDate) {
        const start = startDate.toISOString();
        const end = endDate.toISOString();
        ordersUrl += `?startDate=${start}&endDate=${end}`;
      }
      const ordersRes = await axios.get(ordersUrl);
      setOrdersByDate(ordersRes.data);

      // Fetch order status distribution
      const statusRes = await axios.get(`${API_URL}/analytics/order-status/${tenantId}`);
      setOrderStatus(statusRes.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error loading dashboard data. Check console.');
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    if (startDate && endDate) {
      if (startDate > endDate) {
        alert('Start date must be before end date');
        return;
      }
      fetchDashboardData();
    } else {
      alert('Please select both start and end dates');
    }
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    fetchDashboardData();
  };

  const handleSync = async () => {
    if (window.confirm('Sync data from Shopify? This may take a minute...')) {
      try {
        await axios.post(`${API_URL}/sync/all/${tenantId}`);
        alert('✅ Sync completed! Refreshing data...');
        fetchDashboardData();
      } catch (error) {
        alert('❌ Sync failed. Check console.');
        console.error(error);
      }
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard... ⏳</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div>
          <h1>📊 Analytics Dashboard</h1>
          <p>Real-time insights from your Shopify store</p>
        </div>
        <div className="header-actions">
          <button onClick={handleSync} className="sync-btn">
            🔄 Sync Now
          </button>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      {/* Date Filter */}
      <div className="filter-section">
        <div className="date-filter">
          <div className="date-input-group">
            <label>Start Date:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select start date"
              className="date-picker"
            />
          </div>
          
          <div className="date-input-group">
            <label>End Date:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select end date"
              className="date-picker"
            />
          </div>
          
          <button onClick={handleDateFilter} className="filter-btn">
            📅 Apply Filter
          </button>
          
          <button onClick={handleClearFilter} className="clear-btn">
            ✖ Clear
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <SummaryCard
          title="Total Customers"
          value={summary.totalCustomers || 0}
          icon="👥"
          color="#667eea"
        />
        <SummaryCard
          title="Total Orders"
          value={summary.totalOrders || 0}
          icon="📦"
          color="#f093fb"
        />
        <SummaryCard
          title="Total Revenue"
          value={`$${summary.totalRevenue || 0}`}
          icon="💰"
          color="#4facfe"
        />
        <SummaryCard
          title="Avg Order Value"
          value={`$${summary.avgOrderValue || 0}`}
          icon="📈"
          color="#43e97b"
        />
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Line Chart - Orders Over Time */}
        <div className="chart-box full-width">
          <h3>📈 Orders Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={ordersByDate}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#667eea" 
                strokeWidth={3}
                name="Revenue ($)"
              />
              <Line 
                type="monotone" 
                dataKey="orderCount" 
                stroke="#f093fb" 
                strokeWidth={3}
                name="Orders"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart - Revenue by Date */}
        <div className="chart-box">
          <h3>📊 Revenue by Date</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ordersByDate}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#4facfe" name="Revenue ($)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart - Order Status Distribution */}
        <div className="chart-box">
          <h3>🥧 Order Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={orderStatus}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => `${entry.status}: ${entry.count}`}
              >
                {orderStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Customers Table */}
      <div className="chart-box full-width">
        <h3>🏆 Top 5 Customers by Spend</h3>
        <table className="customers-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Name</th>
              <th>Email</th>
              <th>Total Spent</th>
              <th>Orders</th>
            </tr>
          </thead>
          <tbody>
            {topCustomers.length > 0 ? (
              topCustomers.map((customer, index) => (
                <tr key={index}>
                  <td>
                    <span className="rank-badge">{index + 1}</span>
                  </td>
                  <td>{customer.name}</td>
                  <td>{customer.email}</td>
                  <td className="highlight">${customer.totalSpent}</td>
                  <td>{customer.ordersCount}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>
                  No customers found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Dashboard;